import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import VendorlyLogo from "@/components/VendorlyLogo";
import { ArrowRight, CheckCircle } from "@phosphor-icons/react";
import { toast, Toaster } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [invalid, setInvalid] = useState(false);

  useEffect(() => { if (!token) setInvalid(true); }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    if (password !== confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => nav("/login"), 2500);
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || "Reset failed";
      toast.error(msg);
      if (msg.toLowerCase().includes("expired") || msg.toLowerCase().includes("invalid")) setInvalid(true);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-brand-bg text-brand-text flex flex-col dot-bg" data-testid="reset-page">
      <Toaster position="top-right" richColors />
      <div className="max-w-7xl w-full mx-auto px-6 lg:px-10 py-6">
        <Link to="/" className="inline-flex items-center gap-2">
          <VendorlyLogo size={30} />
          <span className="font-heading font-bold text-xl tracking-tight">Vendorly</span>
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-md bg-white border border-brand-border rounded-3xl p-8 sm:p-10 shadow-[0_10px_40px_rgba(44,42,41,0.06)]">
          {invalid ? (
            <div className="text-center py-4" data-testid="reset-invalid">
              <h1 className="font-heading text-2xl font-bold">Link expired</h1>
              <p className="text-brand-text/70 mt-2">This reset link is invalid or has expired. Request a new one.</p>
              <Link to="/forgot-password" className="mt-8 inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-white rounded-full px-6 py-3 font-semibold" data-testid="reset-new-link">
                Get a new link
              </Link>
            </div>
          ) : done ? (
            <div className="text-center py-4" data-testid="reset-done">
              <CheckCircle size={56} weight="duotone" className="text-brand-success mx-auto" />
              <h1 className="font-heading text-2xl font-bold mt-4">Password reset</h1>
              <p className="text-brand-text/70 mt-2">Redirecting to sign in…</p>
            </div>
          ) : (
            <>
              <div className="text-xs uppercase tracking-[0.2em] text-brand-secondary mb-3">Set new password</div>
              <h1 className="font-heading font-bold text-3xl tracking-tight">Create a new password</h1>
              <p className="text-brand-text/70 mt-2">Choose something you'll remember.</p>
              <form onSubmit={submit} className="mt-8 space-y-4">
                <div>
                  <label className="text-sm font-semibold">New password</label>
                  <input type="password" required minLength={6} value={password} onChange={(e)=>setPassword(e.target.value)}
                    className="mt-1.5 w-full h-12 px-4 rounded-xl bg-brand-bg border border-brand-border focus:border-brand-primary focus:ring-1 focus:ring-brand-primary outline-none transition-colors"
                    data-testid="reset-password" placeholder="Min 6 characters" />
                </div>
                <div>
                  <label className="text-sm font-semibold">Confirm password</label>
                  <input type="password" required value={confirm} onChange={(e)=>setConfirm(e.target.value)}
                    className="mt-1.5 w-full h-12 px-4 rounded-xl bg-brand-bg border border-brand-border focus:border-brand-primary focus:ring-1 focus:ring-brand-primary outline-none transition-colors"
                    data-testid="reset-confirm" placeholder="Re-type" />
                </div>
                <button type="submit" disabled={busy}
                  className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-full bg-brand-primary hover:bg-brand-primaryHover text-white font-semibold transition-colors disabled:opacity-60"
                  data-testid="reset-submit">
                  {busy ? "Updating…" : (<>Set new password <ArrowRight size={18} weight="bold" /></>)}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
